<script lang="ts">
	import { createEventDispatcher, onMount } from "svelte";
	import type { YouTubePlayer, YouTubePlayerStateChangeEvent } from "#lib/types/youtube.js";

	type Props = {
		src?: string;
		autoplay?: boolean;
		style?: string;
		[key: string]: unknown;
	};

	let { src = "", autoplay = false, style, ...restProps }: Props = $props();
	let videoContainer: HTMLDivElement;
	let playerReady = $state(false);
	let player = $state<YouTubePlayer | null>(null);

	const dispatch = createEventDispatcher();

	function load() {
		const yt = window.YT;
		if (videoContainer && yt) {
			player = new yt.Player(videoContainer, {
				videoId: src,
				playerVars: { autoplay, mute: autoplay },
				events: {
					onReady: () => (playerReady = true),
					onStateChange: (event: YouTubePlayerStateChangeEvent) => {
						if (event.data === yt.PlayerState.ENDED) {
							dispatch("ended");
						}
					},
				},
			});
		}
	}

	onMount(() => {
		if (!window._youtubePlayerQueue) {
			window._youtubePlayerQueue = [];
		}
		if (window.YT && window.YT.Player) {
			load();
		} else {
			window._youtubePlayerQueue.push(load);

			if (!window.onYouTubeIframeAPIReady) {
				window.onYouTubeIframeAPIReady = () => {
					window._youtubePlayerQueue.forEach(fn => fn());
					window._youtubePlayerQueue = [];
				};
			}
		}
	});

	$effect(() => {
		const currentPlayer = player as YouTubePlayer | undefined;
		if (!playerReady || !src || !currentPlayer) {
			return;
		}

		if (autoplay) {
			currentPlayer.loadVideoById(src);
		} else {
			currentPlayer.cueVideoById(src);
		}
	});
</script>

<svelte:head>
	<script src="https://www.youtube.com/iframe_api"></script>
</svelte:head>

<div {...restProps} {style} bind:this={videoContainer}></div>
